import apiClient from '@/lib/apiClient';

/** GPT domain-detection result — persists nothing, just previews the company. */
export interface DetectResult {
  company_name: string | null;
  category: string | null;
  confidence: string | null;
}

export interface CompanySettings {
  name: string;
  domain: string;
  category?: string;
}

/** PUT /company/settings upserts the company AND dispatches the research job;
 * it returns the job id the SSE log viewer streams. */
export interface SaveSettingsResult {
  job_id?: string;
  [key: string]: unknown;
}

export interface CompanyRecord {
  name: string;
  domain?: string | null;
  category?: string | null;
  research_status?: string;
  research_job_id?: string | null;
  [key: string]: unknown;
}

/**
 * The company-research surface: detect a company from its domain, save its
 * settings (which kicks off research), and re-run research. The
 * create-workspace modal, the onboarding wizard, and settings all go through
 * here so the detect → save → dispatch sequence lives in exactly one place.
 */
export const companyService = {
  detectDomain(workspaceId: string, domain: string, signal?: AbortSignal): Promise<DetectResult> {
    return apiClient.post<DetectResult>(`/workspaces/${workspaceId}/company/detect-domain`, { domain }, { signal });
  },

  saveSettings(workspaceId: string, settings: CompanySettings): Promise<SaveSettingsResult> {
    return apiClient.put<SaveSettingsResult>(`/workspaces/${workspaceId}/company/settings`, settings);
  },

  getCompany(workspaceId: string): Promise<CompanyRecord> {
    return apiClient.get<CompanyRecord>(`/workspaces/${workspaceId}/company`);
  },

  /** Re-dispatch research for an already-configured company (the banner "Retry"
   * after a failed run). Reuses the saved name/domain/category — no new input. */
  async retryResearch(workspaceId: string): Promise<SaveSettingsResult> {
    const company = await companyService.getCompany(workspaceId);
    return companyService.saveSettings(workspaceId, {
      name: company.name,
      domain: company.domain ?? '',
      category: company.category ?? undefined,
    });
  },
};
